/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The mark for a subheading over an open editor, with both ways back** (`#1423`, Todd 2026-09-23:
 * *revert is "remove my override of the parent", and it should be true everywhere*).
 *
 * A row already had both: a change made only in the draft is undone locally, and a saved override is
 * reverted on the server and then reconciled into the draft (`useDraftRevert`). A **subheading** had
 * only the first, so a saved override of a whole subtree could be reverted in the editor from exactly
 * one place — the section's title — and that was a server write the draft never heard about. The
 * draft kept the override, and Save posted it straight back (the `#1126` defect, one level up).
 *
 * So this is the row's pair, for a subtree:
 *
 * - **stored inherited, draft changed** → `reset` puts the stored value back into the draft. No
 *   server write, because what is stored already is the parent's.
 * - **stored overridden** → the server revert, and once the source has reloaded, `reset` again, so
 *   the draft takes the value the source now inherits. It takes effect now, not on Save, and the
 *   control says so. Any unsaved change inside this subtree goes with it: that is what reverting the
 *   subtree means.
 *
 * `reset` is read at reload time rather than when the revert was pressed, because the caller's
 * closure over the stored value is stale until the page renders with the reloaded source.
 */

import React from 'react';
import type { ArtifactInheritance } from '../../types';
import { pathInheritance, pathsInheritance } from '../../utils/artifact-inheritance';
import { InheritanceMark } from './InheritanceMark';

export interface DraftSectionMarkProps {
  inheritance: ArtifactInheritance | null | undefined;
  /**
   * The subtree this subheading stands over, **or several** where one control writes more than one
   * (a strategy's source pool is `demandSources` and `excludedDemandSources`). `overridden` wins across
   * the set, as in `InheritanceMark`.
   */
  path: string | readonly string[];
  parentName: string | undefined;
  /** The draft's answer: does it override something the stored artifact inherits? */
  overridesInherited: boolean;
  /**
   * What is stored for this subtree now, in any serialisable form. A change to it is how a finished
   * server revert is recognised, the way `useDraftRevert` watches its baseline rows.
   */
  stored: unknown;
  /** Put what is stored for this subtree back into the draft. */
  reset: () => void;
  /** The server revert, resolving once it is written; `null` where the viewer may not edit. */
  onRevert: ((paths: string[]) => Promise<unknown>) | null;
  isReverting: boolean;
}

export const DraftSectionMark: React.FC<DraftSectionMarkProps> = ({
  inheritance,
  path,
  parentName,
  overridesInherited,
  stored,
  reset,
  onRevert,
  isReverting,
}) => {
  const storedKey = JSON.stringify(stored ?? null);
  const pending = React.useRef<string | null>(null);
  const latestReset = React.useRef(reset);
  React.useLayoutEffect(() => {
    latestReset.current = reset;
  });

  React.useEffect(() => {
    if (pending.current === null || pending.current === storedKey) return;
    pending.current = null;
    latestReset.current();
  }, [storedKey]);

  const storedOverride =
    (typeof path === 'string' ? pathInheritance(inheritance, path) : pathsInheritance(inheritance, path)).state ===
    'overridden';
  return (
    <InheritanceMark
      inheritance={inheritance}
      path={path}
      parentName={parentName}
      state={overridesInherited ? 'overridden' : undefined}
      restoreDraft={reset}
      revertsNow={storedOverride}
      isReverting={isReverting}
      onRevert={
        onRevert
          ? (paths) => {
              pending.current = storedKey;
              void onRevert(paths).catch(() => {
                // A failed revert changed nothing stored, so there is nothing to put back.
                pending.current = null;
              });
            }
          : undefined
      }
    />
  );
};
